import { flattenReportRows, revenueCents } from './engine';
import { commissionAmountCents } from './commission';
import { formatReportValue } from './format';
import { formatPeriodLabel } from './periods';
import { METRIC_BY_ID } from './metrics';
import { inferSourceType } from './revenueEvents';
import type { ExportLabels } from './exportCsv';
import type { ReportResult } from './types';

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cell(value: string): string {
  return `<Cell><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`;
}

function row(values: string[]): string {
  return `<Row>${values.map(cell).join('')}</Row>`;
}

function sheet(name: string, rows: string[]): string {
  return `<Worksheet ss:Name="${xmlEscape(name)}"><Table>${rows.join('')}</Table></Worksheet>`;
}

export function reportToExcelXml(result: ReportResult, labels: ExportLabels): string {
  const expanded = new Set(result.rows.map((item) => item.id));
  const visible = [...flattenReportRows(result.rows, expanded), result.totals];

  const headerLabels = result.columns.map((column) => {
    if (column.id === 'practitioner') return labels.practitioner;
    if (column.id === 'service') return labels.service;
    return labels.metricLabels[column.label] ?? column.label;
  });

  const summaryRows = [
    row([labels.title]),
    row([formatPeriodLabel(result.period)]),
    row([]),
    row([labels.summary]),
    ...result.summary.map((card) => {
      const metric = METRIC_BY_ID[card.metricId];
      return row([
        labels.metricLabels[metric?.labelKey ?? ''] ?? card.metricId,
        formatReportValue(card.value, metric?.format ?? 'text', result.currency),
      ]);
    }),
  ];

  const tableRows = [
    row(headerLabels),
    ...visible.map((item) => {
      const cells: string[] = [];
      for (const column of result.columns) {
        if (column.id === 'practitioner') {
          cells.push(item.grouping === 'service' && item.depth > 0 ? '' : item.label);
        } else if (column.id === 'service') {
          cells.push(item.grouping === 'service' || item.grouping === 'subtotal' ? item.label : '');
        } else {
          const metric = METRIC_BY_ID[column.metricId];
          cells.push(
            formatReportValue(
              item.values[column.metricId] ?? null,
              metric?.format ?? 'text',
              result.currency,
            ),
          );
        }
      }
      return row(cells);
    }),
  ];

  const detailRows = [
    row([
      labels.detailHeaders.sourceType,
      labels.detailHeaders.date,
      labels.detailHeaders.practitioner,
      labels.detailHeaders.patient,
      labels.detailHeaders.service,
      labels.detailHeaders.billingMethod,
      labels.detailHeaders.clipsRedeemed,
      labels.detailHeaders.revenuePerClip,
      labels.detailHeaders.recognizedRevenue,
      labels.detailHeaders.commissionPercent,
      labels.detailHeaders.commissionAmount,
    ]),
    ...result.facts
      .filter((fact) => fact.fact_kind === 'appointment')
      .map((fact) => {
      const recognized = revenueCents(fact, result.revenueBasis);
      const currency = fact.currency || result.currency;
      return row([
        inferSourceType(fact),
        fact.starts_at,
        fact.practitioner_name ?? '',
        fact.patient_name ?? '',
        fact.service_name ?? '',
        fact.billing_method ?? '',
        fact.clips_redeemed ? String(fact.clips_redeemed) : '',
        fact.revenue_per_clip_cents == null
          ? ''
          : formatReportValue(fact.revenue_per_clip_cents, 'currency', currency),
        formatReportValue(recognized, 'currency', currency),
        fact.commission_percent == null ? '' : String(fact.commission_percent),
        formatReportValue(
          commissionAmountCents(recognized, fact.commission_percent),
          'currency',
          currency,
        ),
      ]);
    }),
  ];

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${sheet(labels.summary, summaryRows)}
${sheet(labels.title, tableRows)}
${sheet(labels.detail, detailRows)}
</Workbook>`;
}
